import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

const translations = {
  en: {
    // Navbar
    home: "Home",
    courses: "Courses",
    freeResources: "Free Resources",
    videoCourses: "Video Courses",
    categories: "Categories",
    about: "About",
    contact: "Contact",
    faq: "FAQ",
    language: "en",

    // Home Page
    welcomeTitle: "Welcome to COSMO Personality Education",
    welcomeSubtitle: "Unlock your potential with our comprehensive online courses and free educational resources. Learn at your own pace, anywhere, anytime.",
    premiumCourses: "Premium Courses",
    whatWeOffer: "What We Offer",
    comprehensiveLearning: "A comprehensive learning experience tailored for psychology professionals",
    premiumCoursesDescription: "In-depth, expert-led courses with certifications and hands-on training for professional development.",
    freeResourcesDescription: "Access podcasts, mind maps, and live e-books covering the latest research and methodologies.",
    exploreCourses: "Explore Courses",
    accessResources: "Access Resources",

    // Free Resources Page
    freeResourcesTitle: "Free Educational Resources",
    freeResourcesSubtitle: "Explore our comprehensive collection of research articles with multiple learning formats",
    personalityAssessment: "Personality Assessment",
    personalityAssessmentDesc: "Advanced methods and tools for personality evaluation",
    collaborativeAssessment: "Therapeutic Assessment",
    collaborativeAssessmentDesc: "Innovative approaches to collaborative psychological evaluation",
    psychotherapy: "Psychotherapy",
    psychotherapyDesc: "Evidence-based psychotherapy techniques and interventions",
    familyTherapy: "Family Therapy and Systemic Approach",
    familyTherapyDesc: "Specialized approaches to family and systemic therapy",
    articles: "articles",
    backToTopics: "Back to Topics",
    backToHome: "Back to Home",

    // Video Courses Page
    videoCoursesTitle: "Video Courses",
    videoCoursesSubtitle: "Enhance your professional skills with our expert-led video courses",
    watchCourse: "Watch Course",
    hour: "hour",
    hours: "hours",
    buyNow: "Buy Now",
    comingSoon: "Coming Soon",

    // Payment Modal
    purchaseCourse: "Purchase Course",
    paymentMethod: "Payment Method",
    cardNumber: "Card Number",
    cardholderName: "Cardholder Name",
    expiryDate: "Expiry Date",
    securePayment: "Secure payment processing",
    paymentSuccessful: "Payment Successful!",
    courseAvailable: "Your course is now available. Enjoy your learning!",
    processing: "Processing...",
    pay: "Pay",
    continueWithPaypal: "Continue with PayPal",
    paypalRedirect: "You will be redirected to PayPal to complete your purchase.",
    fillAllFields: "Please fill in all fields",

    // Cross-navigation
    viewPremiumCourses: "View Premium Courses",
    viewFreeResources: "View Free Resources",
    viewVideoCourses: "View Video Courses",

    // Language Filter
    filterByLanguage: "Filter by Language",
    allLanguages: "All Languages",
    englishOnly: "English Only",
    italianOnly: "Italian Only",
    noArticlesFound: "No Articles Found",
    noArticlesInLanguage: "No articles available in the selected language filter.",

    // Language Availability
    available: "Available",
    notAvailable: "Not Available",
    availableIn: "Available in",
    availableInYourLanguage: "Available in your language",
    notAvailableInYourLanguage: "Not available in your current language",
    resourcesNotAvailableInLang: "Resources not available in {{lang}}",

    // Resource Actions
    ebook: "Audio & Blog Post",
    mindMap: "Mind Map",
    podcast: "Podcast",
    blogPost: "Audio & Blog Post",
    readEbook: "Read Audio & Blog Post",
    readBlogPost: "Read Audio & Blog Post",
    viewMindMap: "View Mind Map",
    listenPodcast: "Listen Podcast",

    // Instructions
    howToUse: "How to use",
    resourceInstructions: "Each article is available in multiple formats: Text for detailed reading, Mind Map for visual overview, and Podcast for audio learning. Choose your preferred learning style!",

    // About Page
    aboutTitle: "About COSMO Personality Education",
    aboutSubtitle: "Transforming personality assessment through innovation and accessibility",
    ourStory: "Our Story",
    aboutDescription1: "Founded by David Thompson in 2023, COSMO Personality Education was born from a vision to make quality personality assessment education accessible to everyone. With over 15 years of experience in psychology and technology, David recognized the need for a platform that combines innovative assessment methods with user-friendly technology.",
    aboutDescription2: "Today, COSMO Personality serves thousands of psychology professionals worldwide, offering specialized courses and tools from top educators and industry experts in personality assessment.",
    founderCEO: "Founder & CEO",

    // Contact Page
    contactTitle: "Contact Us",
    contactSubtitle: "We'd love to hear from you",
    getInTouch: "Get in Touch",
    email: "Email",
    sendMessage: "Send us a Message",
    name: "Name",
    message: "Message",
    sendBtn: "Send Message",

    // Courses Page
    coursesTitle: "Our Courses",
    coursesSubtitle: "Expand your knowledge with our expert-led courses",
    enrollNow: "Enroll Now",
    students: "students",
    weeks: "weeks",

    // Categories Page
    categoriesTitle: "Course Categories",
    categoriesSubtitle: "Explore our specialized psychology and therapy programs",
    coursesCount: "courses",

    // Footer
    footerDescription: "Empowering minds through accessible and engaging personality assessment education.",
    contactUs: "Contact Us",
    quickLinks: "Quick Links",
    allRightsReserved: "All rights reserved",

    // Course Details
    webDevelopment: "Web Development Fundamentals",
    webDevDescription: "Learn HTML, CSS, and JavaScript basics",
    pid5bf: "PID-5-BF+",
    pid5bfDescription: "This training course offers experienced clinicians a comprehensive guide to understanding, administering, and interpreting the PID-5-BF+, a brief dimensional tool aligned with DSM-5 AMPD and ICD-11 trait models. Through evidence-based instruction, detailed case analysis, and clinically validated frameworks, participants will learn how to move from raw scores to rich diagnostic insight and actionable treatment planning. Based on landmark research by Bach and colleagues, this course translates trait theory into clinical precision.",
    digitalMarketing: "Digital Marketing",
    digitalMarketingDescription: "Learn modern digital marketing strategies",
    closeCourse: "Close Course",

    // Landing Page
    landingHeroTitle: "B2B Platform for Psychology Professionals",
    landingHeroSubtitle: "Supporting psychologists with personality assessment tools and data-driven clinical experience",
    exploreTools: "Explore Assessment Tools",
    bookConsultation: "Book a Consultation",
    whyChooseCosmo: "Why Choose COSMO Personality?",
    evidenceBased: "Evidence-Based",
    comprehensive: "Comprehensive",
    practical: "Practical",
    innovative: "Innovative",

    // COSMO Heads
    normalAttention: "Normal",
    clinicalAttention: "Clinical Attention",
    highClinicalAttention: "High Clinical Attention",
    cosmoOrients: "COSMO Personality orients you with simplicity",
    attentionLevelsExplanation: "COSMO Personality doesn't make diagnoses, but simply guides clinicians with three levels of clinical attention",

    // Video modal
    watchPresentation: "Video Presentation"
  },
  it: {
    // Navbar
    home: "Home",
    courses: "Corsi",
    freeResources: "Risorse Gratuite",
    videoCourses: "Corsi Video",
    categories: "Categorie",
    about: "Chi Siamo",
    contact: "Contatti",
    faq: "FAQ",
    language: "it",

    // Home Page
    welcomeTitle: "Benvenuti a COSMO Personality Education",
    welcomeSubtitle: "Libera il tuo potenziale con i nostri corsi online completi e risorse educative gratuite. Impara al tuo ritmo, ovunque, in qualsiasi momento.",
    premiumCourses: "Corsi Premium",
    whatWeOffer: "Cosa Offriamo",
    comprehensiveLearning: "Un'esperienza di apprendimento completa pensata per professionisti della psicologia",
    premiumCoursesDescription: "Corsi approfonditi guidati da esperti con certificazioni e formazione pratica per lo sviluppo professionale.",
    freeResourcesDescription: "Accedi a podcast, mappe mentali ed e-book interattivi che coprono le ultime ricerche e metodologie.",
    exploreCourses: "Esplora i Corsi",
    accessResources: "Accedi alle Risorse",

    // Free Resources Page
    freeResourcesTitle: "Risorse Educative Gratuite",
    freeResourcesSubtitle: "Esplora la nostra collezione completa di articoli di ricerca con formati di apprendimento multipli",
    personalityAssessment: "Assessment di Personalità",
    personalityAssessmentDesc: "Metodi e strumenti avanzati per la valutazione della personalità",
    collaborativeAssessment: "Assessment Terapeutico",
    collaborativeAssessmentDesc: "Approcci innovativi alla valutazione psicologica collaborativa",
    psychotherapy: "Psicoterapia",
    psychotherapyDesc: "Tecniche e interventi psicoterapeutici basati su evidenze",
    familyTherapy: "Terapia Familiare e Approccio Sistemico",
    familyTherapyDesc: "Approcci specializzati alla terapia familiare e sistemica",
    articles: "articoli",
    backToTopics: "Torna ai Temi",
    backToHome: "Torna alla Home",

    // Video Courses Page
    videoCoursesTitle: "Corsi Video",
    videoCoursesSubtitle: "Migliora le tue competenze professionali con i nostri corsi video condotti da esperti",
    watchCourse: "Guarda il Corso",
    hour: "ora",
    hours: "ore",
    buyNow: "Acquista Ora",
    comingSoon: "Prossimamente",

    // Payment Modal
    purchaseCourse: "Acquista Corso",
    paymentMethod: "Metodo di Pagamento",
    cardNumber: "Numero Carta",
    cardholderName: "Intestatario Carta",
    expiryDate: "Data Scadenza",
    securePayment: "Elaborazione pagamento sicura",
    paymentSuccessful: "Pagamento Riuscito!",
    courseAvailable: "Il tuo corso è ora disponibile. Buon apprendimento!",
    processing: "Elaborazione...",
    pay: "Paga",
    continueWithPaypal: "Continua con PayPal",
    paypalRedirect: "Sarai reindirizzato a PayPal per completare il tuo acquisto.",
    fillAllFields: "Per favore compila tutti i campi",

    // Cross-navigation
    viewPremiumCourses: "Visualizza Corsi Premium",
    viewFreeResources: "Visualizza Risorse Gratuite",
    viewVideoCourses: "Visualizza Corsi Video",

    // Language Filter
    filterByLanguage: "Filtra per Lingua",
    allLanguages: "Tutte le Lingue",
    englishOnly: "Solo Inglese",
    italianOnly: "Solo Italiano",
    noArticlesFound: "Nessun Articolo Trovato",
    noArticlesInLanguage: "Nessun articolo disponibile nel filtro lingua selezionato.",

    // Language Availability
    available: "Disponibile",
    notAvailable: "Non Disponibile",
    availableIn: "Disponibile in",
    availableInYourLanguage: "Disponibile nella tua lingua",
    notAvailableInYourLanguage: "Non disponibile nella tua lingua attuale",
    resourcesNotAvailableInLang: "Risorse non disponibili in {{lang}}",

    // Resource Actions
    ebook: "Audio & Blog Post",
    mindMap: "Mappa Mentale",
    podcast: "Podcast",
    blogPost: "Audio & Blog Post",
    readEbook: "Leggi Audio & Blog Post",
    readBlogPost: "Leggi Audio & Blog Post",
    viewMindMap: "Visualizza Mappa Mentale",
    listenPodcast: "Ascolta Podcast",

    // Instructions
    howToUse: "Come usare",
    resourceInstructions: "Ogni articolo è disponibile in più formati: Testo per lettura dettagliata, Mappa Mentale per panoramica visiva, e Podcast per apprendimento audio. Scegli il tuo stile di apprendimento preferito!",

    // About Page
    aboutTitle: "Chi è COSMO Personality Education",
    aboutSubtitle: "Trasformiamo la valutazione della personalità attraverso innovazione e accessibilità",
    ourStory: "La Nostra Storia",
    aboutDescription1: "Fondata da David Thompson nel 2023, COSMO Personality Education è nata dalla visione di rendere l'educazione sulla valutazione della personalità di qualità accessibile a tutti. Con oltre 15 anni di esperienza nella psicologia e nella tecnologia, David ha riconosciuto la necessità di una piattaforma che combini metodi di valutazione innovativi con tecnologia user-friendly.",
    aboutDescription2: "Oggi, COSMO Personality serve migliaia di professionisti della psicologia in tutto il mondo, offrendo corsi specializzati e strumenti da educatori di punta ed esperti del settore nella valutazione della personalità.",
    founderCEO: "Fondatore & CEO",

    // Contact Page
    contactTitle: "Contattaci",
    contactSubtitle: "Ci piacerebbe sentirti",
    getInTouch: "Mettiti in Contatto",
    email: "Email",
    sendMessage: "Inviaci un Messaggio",
    name: "Nome",
    message: "Messaggio",
    sendBtn: "Invia Messaggio",

    // Courses Page
    coursesTitle: "I Nostri Corsi",
    coursesSubtitle: "Espandi le tue conoscenze con i nostri corsi guidati da esperti",
    enrollNow: "Iscriviti Ora",
    students: "studenti",
    weeks: "settimane",

    // Categories Page
    categoriesTitle: "Categorie dei Corsi",
    categoriesSubtitle: "Esplora i nostri programmi specializzati di psicologia e terapia",
    coursesCount: "corsi",

    // Footer
    footerDescription: "Potenziamo le menti attraverso un'educazione accessibile e coinvolgente sulla valutazione della personalità.",
    contactUs: "Contattaci",
    quickLinks: "Link Rapidi",
    allRightsReserved: "Tutti i diritti riservati",

    // Course Details
    webDevelopment: "Fondamenti di Sviluppo Web",
    webDevDescription: "Impara le basi di HTML, CSS e JavaScript",
    pid5bf: "PID-5-BF+",
    pid5bfDescription: "Questo corso di formazione offre ai clinici esperti una guida completa per comprendere, amministrare e interpretare il PID-5-BF+, uno strumento dimensionale breve allineato con i modelli di tratti DSM-5 AMPD e ICD-11. Attraverso istruzioni basate su evidenze, analisi dettagliate di casi e framework clinicamente validati, i partecipanti impareranno come passare dai punteggi grezzi a intuizioni diagnostiche ricche e pianificazione del trattamento attuabile. Basato sulla ricerca pionieristica di Bach e colleghi, questo corso traduce la teoria dei tratti in precisione clinica.",
    digitalMarketing: "Marketing Digitale",
    digitalMarketingDescription: "Impara le strategie moderne di marketing digitale",
    closeCourse: "Chiudi Corso",

    // Landing Page
    landingHeroTitle: "Piattaforma B2B per Professionisti della Psicologia",
    landingHeroSubtitle: "Supporto per psicologi con strumenti di valutazione della personalità ed esperienza clinica basata sui dati",
    exploreTools: "Esplora gli Strumenti di Valutazione",
    bookConsultation: "Prenota una Consulenza",
    whyChooseCosmo: "Perché Scegliere COSMO Personality?",
    evidenceBased: "Basato su Evidenze",
    comprehensive: "Completo",
    practical: "Pratico",
    innovative: "Innovativo",

    // COSMO Heads
    normalAttention: "Normale",
    clinicalAttention: "Attenzione Clinica",
    highClinicalAttention: "Alta Attenzione Clinica",
    cosmoOrients: "COSMO Personality ti orienta con semplicità",
    attentionLevelsExplanation: "COSMO Personality non fa diagnosi, ma orienta con semplicità i clinici attraverso tre livelli di attenzione clinica",

    // Video modal
    watchPresentation: "Video Presentazione"
  }
};

export const LanguageProvider = ({ children }) => {
  // Store current location to prevent redirect on language change
  const [currentPath, setCurrentPath] = useState(window.location.hash);
  const [language, setLanguage] = useState('en');

  // Update currentPath when location changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.hash);
    };

    window.addEventListener('hashchange', handleLocationChange);
    return () => {
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'it' : 'en');
  };

  const t = (key, params = {}) => {
    let translation = translations[language][key] || key;
    
    // Simple template replacement for parameters
    if (params && typeof translation === 'string') {
      Object.keys(params).forEach(param => {
        translation = translation.replace(`{{${param}}}`, params[param]);
      });
    }
    
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t, currentPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;